import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ControlParams } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export class ColorEdit extends EditableControl {
    //#region Fields
    public override innerElement: HTMLInputElement;

    private _originalValue: string;
    private _changeListener: EventListener;
    private _listenerFocus: EventListener;
    private _listenerBlur: EventListener;
    //#endregion Fields

    //#region Constructors
    constructor(params: ControlParams) {
        super(params);
        this.innerElement = createElement({ type: "input", attrs: { "type": "color" } }) as HTMLInputElement;

        this.element.classList.add("ColorEdit", "Empty");
        this.element.appendChild(this.innerElement);
        this._setCaptionReference(this.innerElement);

        this.setListeners();
    }
    //#endregion Constructors

    //#region Properties
    /**
     * Vrátí hodnotu editu
     */
    public override get controlValue(): string {
        return this._originalValue;
    }

    /**
     * Nastavi hodnotu editu
     */
    public override set controlValue(value: string) {
        if (value) {
            this.innerElement.value = value;
        } else {
            this.innerElement.value = "#000000"; // Defaultní hodnota dle standardu. Nepoužívá se null, aby se v konzoli nezobrazovaly chyby
        }

        this._originalValue = value;

        this.element.classList.toggle("Empty", (value == null)); // Umožňuje zobrazit transparentní prvek u prázdné hodnoty
    }
    //#endregion Properties

    //#region Methods

    /**
     * Zaregistruje potřebné listenery
     */
    public setListeners(): void {
        this.innerElement.addEventListener("change", this._changeListener = () => {
            this._changeEventHandler();
        });

        this.innerElement.addEventListener("focus", this._listenerFocus = (e: FocusEvent) => {
            this.getFocus.trigger(this, e);
            this._focusEventHandler();
        });

        this.innerElement.addEventListener("blur", this._listenerBlur = (e: FocusEvent) => {
            this.lostFocus.trigger(this, e);
            this._blurEventHandler();
        });
    }

    /**
     * Událost vyvolaná při kliknutí na element ColorEditu
     * @param _sender - this
     * @param event - událost, obsahující cíl kliknutí
     */
    protected override _editableControlClicked(_sender: EditableControl, event: MouseEvent): void {
        // Je nutné ověřit, zda nešlo o klik na překrývající "transparentní" prvek a na základě toho zpřístupnit detail k editaci a otevřít ColorPicker
        if (event.target !== this.innerElement) {
            if (this._originalValue == null && !this.isEditing) { // Nejsme v editaci a nemáme nastavenou hodnotu - musíme počkat, až se promítnout změny stylů, aby byla získána správná barva
                PromiseUtils.delay(200).then(() => {
                    this.innerElement.click();
                });
            }
        } else {
            if (this._originalValue == null) {
                // Získáme aktuální barvu EC podle tématu a tu nastavíme jako default
                this.innerElement.value = DataConverter.rgbToHex(window.getComputedStyle(this.element).backgroundColor, this._getParentBackgroundColor(this.parentElement));
            }
        }
    }

    /**
     * Vrací barvu podkladu, na kterém je komponenta umístěna - používá se ke dopočítání správné hex barvy při použití alfa kanálu
     * @param element - prvek, na kterém je umístěn ColorEdit
     */
    protected _getParentBackgroundColor(element: HTMLElement): string {
        if (element == null) {
            return null;
        }

        const color = window.getComputedStyle(element).backgroundColor;

        if (color === "rgba(0, 0, 0, 0)" || color === "transparent") { // U transparentního prvku musíme jít na jeho rodiče a tak dále
            return this._getParentBackgroundColor(element.parentElement);
        }

        return color;
    }

    /**
     * Událost vyvolaná při změně hodnoty
     */
    protected _changeEventHandler(): void {
        this.controlValue = this.innerElement.value;

        this.valueChanged.trigger(this, { Sender: this });
    }
    /**
     * Událost vyvolaná při fokusu
     */
    protected _focusEventHandler(): void {
        if (this.enabled)
            this.element.classList.add("focused");
    }
    /**
     * Událost vyvolaná při blur
     */
    protected _blurEventHandler(): void {
        this.element.classList.remove("focused");
    }

    /**
     * Na základě vstupu povolí nebo zakáže editaci
     * @param value - true/false
     */
    protected override _setEnabledInput(value: boolean): void {
        // ReadOnly z předka (EditableControl) umožňuje v tomto případě měnit hodnoty
        if (this.innerElement && ("disabled" in this.innerElement)) {
            (this.innerElement as any)["disabled"] = !value;
        }
    }

    public override startEdit(): void {
        // V chrome se bez timeoutu zobrazuje picker v levém horním rohu okna, namísto u inputu, jak to dělá při běžné editaci. Ve Firefoxu se zobrazuje v levém horním rohu vždy.
        setTimeout(() => {
            this.innerElement.click();
        }, 0);
    }

    //destruktor
    public override dispose(): void {
        this.innerElement.removeEventListener("change", this._changeListener);
        this.innerElement.removeEventListener("focus", this._listenerFocus);
        this.innerElement.removeEventListener("blur", this._listenerBlur);
        super.dispose();
    }

    //#endregion Methods
}
